import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Tabs,
  Tab,
  Input,
  Textarea,
  Progress,
  Chip,
  Divider,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  addToast,
} from "@heroui/react";
import {
  Upload,
  FileText,
  Database,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useState, useRef } from "react";

import {
  uploadTextData,
  ingestUploadedData,
  uploadFilesWithProgress,
} from "../../../api/upload";
import type { UploadStep, UploadTextPayload } from "../../../api/upload.type";

export const Route = createFileRoute("/(authenticated)/knowledge-base/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [isIngesting, setIsIngesting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [textData, setTextData] = useState({ text: "", description: "" });
  const [isUploadingText, setIsUploadingText] = useState(false);

  // 파일 크기 포매팅 함수
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return "0 B";
    } else if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    } else {
      return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
    }
  };

  // 파일 업로드 상태
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadStates, setUploadStates] = useState<Map<string, UploadStep>>(
    new Map()
  );
  const [isUploading, setIsUploading] = useState(false);
  const [completedFiles, setCompletedFiles] = useState<Set<string>>(new Set());
  const [failedFiles, setFailedFiles] = useState<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 인제스천 확인 모달 열기
  const handleOpenConfirmModal = () => {
    setIsConfirmModalOpen(true);
  };

  // 인제스천 시작
  const handleStartIngestion = async () => {
    setIsConfirmModalOpen(false);
    setIsIngesting(true);
    try {
      const result = await ingestUploadedData();
      addToast({
        title: "인제스천이 시작되었습니다!",
        description: `작업 ID: ${result.data.ingestionJobId}`,
      });
    } catch (error) {
      addToast({
        title: "인제스천 시작에 실패했습니다.",
        description: error instanceof Error ? error.message : "알 수 없는 오류",
      });
    } finally {
      setIsIngesting(false);
    }
  };

  // 텍스트 데이터 업로드
  const handleTextUpload = async () => {
    if (!textData.text.trim() || !textData.description.trim()) {
      addToast({
        title: "텍스트와 설명을 모두 입력해주세요.",
        color: "danger",
      });
      return;
    }

    setIsUploadingText(true);
    try {
      await uploadTextData(textData as UploadTextPayload);
      addToast({
        title: "텍스트 데이터가 업로드되었습니다!",
        color: "success",
      });
      setTextData({ text: "", description: "" });
    } catch (error) {
      addToast({
        title: "텍스트 업로드에 실패했습니다.",
        description: error instanceof Error ? error.message : "알 수 없는 오류",
      });
    } finally {
      setIsUploadingText(false);
    }
  };

  // 파일 선택
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // 파일이 선택된 경우에만 상태 업데이트
    if (files.length > 0) {
      setSelectedFiles(files);
      setUploadStates(new Map());
      setCompletedFiles(new Set());
      setFailedFiles(new Set());
    }
    // 아무 파일도 선택하지 않은 경우 기존 상태 유지
  };

  // 파일 제거
  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 파일 업로드 시작
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;

    // 미완료된 파일들만 업로드
    const pendingFiles = selectedFiles.filter(
      (file) => !completedFiles.has(file.name) && !failedFiles.has(file.name)
    );

    if (pendingFiles.length === 0) {
      addToast({
        title: "업로드할 파일이 없습니다.",
        color: "warning",
      });
      return;
    }

    setIsUploading(true);
    abortControllerRef.current = new AbortController();

    try {
      for await (const progress of uploadFilesWithProgress(
        pendingFiles,
        abortControllerRef.current.signal
      )) {
        setUploadStates(new Map([["all", progress]]));

        if (progress.state === "completed" && progress.result) {
          addToast({
            title: `${progress.result.totalFiles}개 파일 업로드 완료!`,
            color: "success",
          });
          setCompletedFiles(
            new Set([...completedFiles, ...pendingFiles.map((f) => f.name)])
          );
          setIsUploading(false);
          break;
        } else if (progress.state === "failed") {
          addToast({
            title: "파일 업로드에 실패했습니다.",
            description: progress.error,
            color: "danger",
          });

          setFailedFiles(
            new Set([...failedFiles, ...pendingFiles.map((f) => f.name)])
          );
          setIsUploading(false);
          break;
        } else if (progress.state === "aborted") {
          addToast({
            title: "파일 업로드가 중단되었습니다.",
            color: "warning",
          });
          setIsUploading(false);
          break;
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      addToast({
        title: "업로드 중 오류가 발생했습니다.",
        description: error instanceof Error ? error.message : "알 수 없는 오류",
        color: "danger",
      });
      setIsUploading(false);
    }
  };

  // 업로드 중단
  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsUploading(false);
    }
  };

  // 파일 목록 초기화
  const handleResetFiles = () => {
    setSelectedFiles([]);
    setUploadStates(new Map());
    setCompletedFiles(new Set());
    setFailedFiles(new Set());
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 파일 상태 아이콘
  const getFileStatusIcon = (fileName: string) => {
    if (completedFiles.has(fileName)) {
      return <CheckCircle className="w-4 h-4 text-success" />;
    }
    if (failedFiles.has(fileName)) {
      return <AlertCircle className="w-4 h-4 text-danger" />;
    }
    return <FileText className="w-4 h-4 text-default-500" />;
  };

  // 파일 상태 칩
  const getFileStatusChip = (fileName: string) => {
    if (completedFiles.has(fileName)) {
      return (
        <Chip size="sm" color="success" variant="flat">
          완료
        </Chip>
      );
    }
    if (failedFiles.has(fileName)) {
      return (
        <Chip size="sm" color="danger" variant="flat">
          실패
        </Chip>
      );
    }
    return (
      <Chip size="sm" color="default" variant="flat">
        대기
      </Chip>
    );
  };

  const uploadProgress = uploadStates.get("all");

  return (
    <div className="container mx-auto p-2 space-y-6">
      {/* 제목과 액션 버튼 Row */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold">Knowledge Base 관리</h1>
          <p className="text-sm text-default-500">
            텍스트 데이터와 파일을 업로드하여 지식 기반을 구축하세요
          </p>
        </div>
        <Button
          color="primary"
          variant="solid"
          size="lg"
          startContent={
            isIngesting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Database className="w-5 h-5" />
            )
          }
          onPress={handleOpenConfirmModal}
          isLoading={isIngesting}
          isDisabled={isIngesting}
          className="w-full md:w-auto"
        >
          {isIngesting ? "인제스천 진행 중..." : "인제스천 시작"}
        </Button>
      </div>

      {/* 메인 카드 */}
      <Card shadow="sm">
        <CardHeader className="pb-3">
          <div className="flex items-center space-x-2">
            <Chip size="lg" variant="flat">
              업로드 관리
            </Chip>
          </div>
        </CardHeader>
        <Divider />
        <CardBody className="p-3">
          <Tabs aria-label="Knowledge Base 업로드 옵션">
            {/* 텍스트 데이터 업로드 탭 */}
            <Tab
              key="text"
              title={
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>텍스트 데이터 업로드</span>
                </div>
              }
            >
              <div className="p-2 space-y-4">
                <div className="space-y-4">
                  <Input
                    label="설명"
                    placeholder="텍스트 데이터에 대한 설명을 입력하세요"
                    value={textData.description}
                    onValueChange={(value) =>
                      setTextData({ ...textData, description: value })
                    }
                    isRequired
                  />
                  <Textarea
                    label="텍스트 내용"
                    placeholder="업로드할 텍스트 내용을 입력하세요"
                    value={textData.text}
                    onValueChange={(value) =>
                      setTextData({ ...textData, text: value })
                    }
                    minRows={10}
                    isRequired
                  />
                  <Button
                    color="primary"
                    size="lg"
                    startContent={
                      isUploadingText ? null : <Upload className="w-4 h-4" />
                    }
                    onPress={handleTextUpload}
                    isLoading={isUploadingText}
                    isDisabled={
                      isUploadingText ||
                      !textData.text.trim() ||
                      !textData.description.trim()
                    }
                    className="w-full"
                  >
                    {isUploadingText ? "업로드 중..." : "텍스트 업로드"}
                  </Button>
                </div>
              </div>
            </Tab>

            {/* 파일 업로드 탭 */}
            <Tab
              key="files"
              title={
                <div className="flex items-center space-x-2">
                  <Upload className="w-4 h-4" />
                  <span>파일 업로드</span>
                </div>
              }
            >
              <div className="p-2 space-y-6">
                {/* 파일 선택 */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      파일 선택
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-default-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary-50 file:text-primary-700
                        hover:file:bg-primary-100
                        dark:file:bg-primary-900/20 dark:file:text-primary-300
                        dark:hover:file:bg-primary-900/30"
                    />
                  </div>

                  {/* 선택된 파일 목록 */}
                  {selectedFiles.length > 0 && (
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <h3 className="text-lg font-semibold">
                          선택된 파일 ({selectedFiles.length}개)
                        </h3>
                        <Button
                          size="sm"
                          variant="light"
                          color="danger"
                          startContent={<Trash2 className="w-4 h-4" />}
                          onPress={handleResetFiles}
                        >
                          전체 삭제
                        </Button>
                      </CardHeader>
                      <CardBody className="pt-0">
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {selectedFiles.map((file, index) => (
                            <div
                              key={`${file.name}-${index}`}
                              className="flex items-center justify-between p-3 bg-default-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {getFileStatusIcon(file.name)}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-default-500">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                                {getFileStatusChip(file.name)}
                              </div>
                              <Button
                                size="sm"
                                variant="light"
                                color="danger"
                                isIconOnly
                                onPress={() => handleRemoveFile(index)}
                                isDisabled={isUploading}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 p-3 bg-default-100 rounded-lg">
                          <div className="flex justify-between text-sm">
                            <span>총 크기:</span>
                            <span>
                              {formatFileSize(
                                selectedFiles.reduce(
                                  (sum, f) => sum + f.size,
                                  0
                                )
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span>완료:</span>
                            <span className="text-success">
                              {completedFiles.size}개
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span>실패:</span>
                            <span className="text-danger">
                              {failedFiles.size}개
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* 업로드 진행률 */}
                  {uploadProgress && (
                    <Card>
                      <CardBody className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {uploadProgress.state === "uploading" && (
                              <Loader2 className="w-5 h-5 animate-spin text-primary" />
                            )}
                            {uploadProgress.state === "completed" && (
                              <CheckCircle className="w-5 h-5 text-success" />
                            )}
                            {uploadProgress.state === "failed" && (
                              <AlertCircle className="w-5 h-5 text-danger" />
                            )}
                            <span className="font-medium">
                              {uploadProgress.state === "preparing" &&
                                "준비 중..."}
                              {uploadProgress.state === "uploading" &&
                                "업로드 중..."}
                              {uploadProgress.state === "completed" &&
                                "업로드 완료!"}
                              {uploadProgress.state === "failed" &&
                                "업로드 실패"}
                              {uploadProgress.state === "aborted" &&
                                "업로드 중단됨"}
                            </span>
                          </div>
                          <span className="text-sm text-default-500">
                            {uploadProgress.progress.toFixed(0)}%
                          </span>
                        </div>

                        <Progress
                          value={uploadProgress.progress}
                          color={
                            uploadProgress.state === "completed"
                              ? "success"
                              : uploadProgress.state === "failed" ||
                                uploadProgress.state === "aborted"
                              ? "danger"
                              : "primary"
                          }
                          size="md"
                          className="w-full"
                        />

                        {uploadProgress.currentFile && (
                          <p className="text-xs text-default-500">
                            현재 파일: {uploadProgress.currentFile}
                          </p>
                        )}

                        {uploadProgress.error && (
                          <p className="text-xs text-danger">
                            오류: {uploadProgress.error}
                          </p>
                        )}
                      </CardBody>
                    </Card>
                  )}

                  {/* 액션 버튼들 */}
                  <div className="flex gap-3">
                    {!isUploading ? (
                      <Button
                        color="primary"
                        size="lg"
                        startContent={<Upload className="w-4 h-4" />}
                        onPress={handleFileUpload}
                        isDisabled={selectedFiles.length === 0}
                        className="flex-1"
                      >
                        업로드 시작
                      </Button>
                    ) : (
                      <Button
                        color="danger"
                        variant="bordered"
                        size="lg"
                        startContent={<X className="w-4 h-4" />}
                        onPress={handleCancelUpload}
                        className="flex-1"
                      >
                        업로드 중단
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Tab>
          </Tabs>
        </CardBody>
      </Card>

      {/* 인제스천 확인 모달 */}
      <Modal
        isOpen={isConfirmModalOpen}
        onOpenChange={setIsConfirmModalOpen}
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        backdrop="blur"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-warning" />
              <span>인제스천 시작</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p className="text-sm">
                업로드된 모든 텍스트 데이터와 파일을 기반으로 지식 기반을
                구축합니다.
              </p>
              <div className="p-3 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-warning-700 dark:text-warning-300">
                      주의사항
                    </p>
                    <ul className="text-xs text-warning-600 dark:text-warning-400 space-y-1">
                      <li>• 이 작업은 시작되면 중단할 수 없습니다</li>
                      <li>
                        • 대량의 데이터 처리 시 시간이 오래 걸릴 수 있습니다
                      </li>
                      <li>• 처리 중에는 새로운 파일을 업로드하지 마세요</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="text-sm font-medium">
                정말로 인제스천을 시작하시겠습니까?
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="flat"
              onPress={() => setIsConfirmModalOpen(false)}
              isDisabled={isIngesting}
            >
              취소
            </Button>
            <Button
              color="primary"
              onPress={handleStartIngestion}
              isLoading={isIngesting}
              startContent={
                !isIngesting ? <Database className="w-4 h-4" /> : undefined
              }
            >
              {isIngesting ? "시작 중..." : "인제스천 시작"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
